import { createContext, useContext } from "react"

import type { FairUseSavedPanel } from "./fair-use-settings-utils"
import type { FairUseConfigFormState } from "./fair-use-utils"
import type { FairUseConfigResponse, FairUseConfigUpdate } from "@/api/admin/fair-use"

// The shared edit state behind the Fair use area's config tabs.
//
// One document, one hash, several tabs: Plans & pricing, Settings and the lead
// allowance on Users & licences all write the SAME fair-use config, and the
// backend's optimistic-concurrency check is per document. State therefore lives
// above the tab switch — see `fair-use-config-form-provider.tsx` — and each tab
// reads it through {@link useFairUseConfigForm}. A tab holding its own copy
// would lose it the moment the tab unmounted, and would send whichever
// `expectedHash` it had captured when it first mounted.

/**
 * Who is saving. Each id is one Save button on one tab; the ids are separate so
 * a Save clears only its own dirty flag and overlays only its own fields.
 *
 * `limits` and `plans` are the two writers the config document itself
 * distinguishes; `leadAllowance` is the lead approval amount on Users &
 * licences, a delegable field of its own with a Save of its own next to the
 * lead roster. Each id builds its own payload and reads back only its own
 * fields, so an unsaved edit on one tab is never persisted by another tab's
 * Save.
 */
export type FairUseConfigPanelId = FairUseSavedPanel

export const FAIR_USE_CONFIG_PANEL_IDS: readonly FairUseConfigPanelId[] = [
  "limits",
  "plans",
  "leadAllowance",
]

export type FairUseConfigFormValue = {
  /**
   * The edited document, or null while the first GET is in flight. Panels
   * guard on it rather than receiving a half-built form.
   */
  form: FairUseConfigFormState | null
  /** The server's `{config, hash, downgrade_model_options, entitlement_schema}`. */
  data: FairUseConfigResponse | undefined
  isLoading: boolean
  /** Mirrors `PLATFORM_ONLY_CONFIG_FIELDS` on the backend. */
  isPlatformAdmin: boolean
  /** A 409 on the last save: the banner is up and a reload is offered. */
  hasConflict: boolean
  dirty: Record<FairUseConfigPanelId, boolean>
  savingPanel: FairUseConfigPanelId | null
  updateForm: (panel: FairUseConfigPanelId, patch: Partial<FairUseConfigFormState>) => void
  /**
   * Send `updates` with the document's CURRENT hash. A null `updates` (the
   * panel is not in a saveable state) is a no-op, as is a save while another
   * panel is already writing.
   */
  save: (panel: FairUseConfigPanelId, updates: FairUseConfigUpdate | null) => void
  /** Drop every unsaved edit and re-read the document after a conflict. */
  reloadAfterConflict: () => void
}

export const FairUseConfigFormContext = createContext<FairUseConfigFormValue | null>(null)

export function useFairUseConfigForm(): FairUseConfigFormValue {
  const value = useContext(FairUseConfigFormContext)
  if (!value) {
    throw new Error("useFairUseConfigForm must be used inside <FairUseConfigFormProvider>")
  }
  return value
}
